import { useState, useEffect } from "react";
import { SimpleMarkdownEditor } from "@/zapi-admin/shared/components/SimpleMarkdownEditor/SimpleMarkdownEditor";
import $api from "@/zapi-admin/shared/api/axios";
import { toast } from "sonner";
import { BenchmarkManagement } from "./BenchmarkManagement";
import { Gear, PencilSimple, Trash, Plus } from "@phosphor-icons/react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Popover,
  PopoverContent,
  PopoverTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from "@libs/zapi-ui/shadcn";
import { FigmaTypography, isEnterSubmit } from "@libs/zapi-ui/custom";

type Claim = {
  claimmd: string;
  source: { name: string; id: string } | null;
  sourceType: "document" | "url" | null;
};

type TestHistoryItem = {
  id: number;
  totalClaims: number;
  matchedClaims: number | null;
  expectedClaims: number | null;
  benchmarkName: string | null;
  createdAt: string;
  executionTimeMs: number | null;
};

type Benchmark = {
  id: number;
  name: string;
  expectedClaimsCount: number;
  createdAt: string;
  chapterContentPreview: string;
};

type ClaimComparison = {
  benchmarkClaim: string | null;
  benchmarkClaimMd: string | null;
  actualClaim: string | null;
  actualClaimMd: string | null;
  matched: boolean;
};

type Comparison = {
  expectedCount: number;
  matchedCount: number;
  claimComparisons?: ClaimComparison[];
};

type ViewMode = "list" | "detail" | "benchmarks";

export function ClaimsFindingTab() {
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [tests, setTests] = useState<TestHistoryItem[]>([]);
  const [isLoadingTests, setIsLoadingTests] = useState(true);

  const [chapterContent, setChapterContent] = useState("");
  const [isRunning, setIsRunning] = useState(false);
  const [comparison, setComparison] = useState<Comparison | null>(null);
  const [selectedHighlight, setSelectedHighlight] = useState<string | null>(null);

  const [isNewTestModalOpen, setIsNewTestModalOpen] = useState(false);
  const [selectedBenchmarkId, setSelectedBenchmarkId] = useState<number | null>(null);

  const [isCreateBenchmarkModalOpen, setIsCreateBenchmarkModalOpen] = useState(false);
  const [benchmarks, setBenchmarks] = useState<Benchmark[]>([]);
  const [isLoadingBenchmarks, setIsLoadingBenchmarks] = useState(false);

  const [benchmarkName, setBenchmarkName] = useState("");
  const [benchmarkContent, setBenchmarkContent] = useState("");
  const [benchmarkExpectedClaims, setBenchmarkExpectedClaims] = useState<Claim[]>([]);
  const [isCreatingBenchmark, setIsCreatingBenchmark] = useState(false);
  const [selectedBenchmarkClaimIndex, setSelectedBenchmarkClaimIndex] = useState<number | null>(null);

  const [editingClaimIndex, setEditingClaimIndex] = useState<number | null>(null);
  const [editingClaimText, setEditingClaimText] = useState("");
  const [isAddingClaim, setIsAddingClaim] = useState(false);
  const [newClaimText, setNewClaimText] = useState("");

  const [filterBenchmark, setFilterBenchmark] = useState<string>("all");
  const [filterDateFrom, setFilterDateFrom] = useState<string>("");
  const [filterDateTo, setFilterDateTo] = useState<string>("");
  const [isChartModalOpen, setIsChartModalOpen] = useState(false);
  const [hiddenBenchmarks, setHiddenBenchmarks] = useState<Set<string>>(new Set());

  const toggleBenchmarkVisibility = (name: string) => {
    setHiddenBenchmarks((prev) => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });
  };

  useEffect(() => {
    fetchTests();
    fetchBenchmarks();
  }, []);

  const fetchTests = async () => {
    try {
      setIsLoadingTests(true);
      const response = await $api.get("/playground/claims-extraction-tests");
      if (response.data.success) {
        setTests(response.data.tests);
      }
    } catch (error) {
      console.error("Error fetching test history:", error);
      toast.error("Failed to load test history");
    } finally {
      setIsLoadingTests(false);
    }
  };

  const fetchBenchmarks = async () => {
    try {
      setIsLoadingBenchmarks(true);
      const response = await $api.get("/playground/benchmarks");
      if (response.data.success) {
        setBenchmarks(response.data.benchmarks);
      }
    } catch (error) {
      console.error("Error fetching benchmarks:", error);
      toast.error("Failed to load benchmarks");
    } finally {
      setIsLoadingBenchmarks(false);
    }
  };

  const handleLoadTest = async (testId: number) => {
    try {
      const response = await $api.get(`/playground/claims-extraction-tests/${testId}`);

      if (response.data.success) {
        const { test } = response.data;
        setChapterContent(test.chapterContent);
        console.log(response.data.comparison);
        setComparison(response.data.comparison || null);
        setSelectedHighlight(null);
        setViewMode("detail");
      } else {
        toast.error("Failed to load test");
      }
    } catch (error) {
      console.error("Error loading test:", error);
      toast.error("Failed to load test");
    }
  };

  const handleRunNewTest = () => {
    setIsNewTestModalOpen(true);
    setSelectedBenchmarkId(null);
  };

  const handleCreateBenchmark = () => {
    setIsCreateBenchmarkModalOpen(true);
    setBenchmarkName("");
    setBenchmarkContent("");
    setBenchmarkExpectedClaims([]);
    setSelectedBenchmarkClaimIndex(null);
  };

  const handleManageBenchmarks = () => {
    setViewMode("benchmarks");
  };

  const handleSelectBenchmarkClaim = (idx: number) => {
    setSelectedBenchmarkClaimIndex(selectedBenchmarkClaimIndex === idx ? null : idx);
  };

  const handleAddClaim = () => {
    if (!newClaimText.trim()) {
      toast.error("Please enter claim markdown");
      return;
    }

    const newClaim: Claim = {
      claimmd: newClaimText.trim(),
      source: null,
      sourceType: null
    };

    setBenchmarkExpectedClaims([...benchmarkExpectedClaims, newClaim]);
    setNewClaimText("");
    setIsAddingClaim(false);
    toast.success("Claim added");
  };

  const handleStartEditClaim = (idx: number) => {
    setEditingClaimIndex(idx);
    setEditingClaimText(benchmarkExpectedClaims[idx].claimmd);
    setSelectedBenchmarkClaimIndex(null);
  };

  const handleSaveClaimEdit = () => {
    if (editingClaimIndex === null) return;
    if (!editingClaimText.trim()) {
      toast.error("Claim markdown cannot be empty");
      return;
    }

    const updatedClaims = [...benchmarkExpectedClaims];
    updatedClaims[editingClaimIndex] = {
      ...updatedClaims[editingClaimIndex],
      claimmd: editingClaimText.trim()
    };

    setSelectedBenchmarkClaimIndex(null);
    setBenchmarkExpectedClaims(updatedClaims);
    setEditingClaimIndex(null);
    setEditingClaimText("");
    toast.success("Claim updated");
  };

  const handleCancelClaimEdit = () => {
    setEditingClaimIndex(null);
    setEditingClaimText("");
  };

  const handleDeleteClaim = (idx: number) => {
    const updatedClaims = benchmarkExpectedClaims.filter((_, i) => i !== idx);
    setBenchmarkExpectedClaims(updatedClaims);
    setSelectedBenchmarkClaimIndex(null);
    toast.success("Claim deleted");
  };

  const handleGenerateExpectedClaims = async () => {
    if (!benchmarkContent.trim()) {
      toast.error("Please enter benchmark content first");
      return;
    }

    setIsCreatingBenchmark(true);

    try {
      const response = await $api.post("/playground/test-claims-extraction", {
        chapterContent: benchmarkContent
      });

      if (response.data.success) {
        setBenchmarkExpectedClaims(response.data.claims);
        setSelectedBenchmarkClaimIndex(null);
        toast.success(`Generated ${response.data.claims.length} expected claims`);
      } else {
        toast.error("Failed to generate claims");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to generate claims");
    } finally {
      setIsCreatingBenchmark(false);
    }
  };

  const handleSaveBenchmark = async () => {
    if (!benchmarkName.trim()) {
      toast.error("Please enter a benchmark name");
      return;
    }

    if (!benchmarkContent.trim()) {
      toast.error("Please enter benchmark content");
      return;
    }

    if (benchmarkExpectedClaims.length === 0) {
      toast.error("Please generate expected claims first");
      return;
    }

    setIsCreatingBenchmark(true);

    try {
      const response = await $api.post("/playground/benchmarks", {
        name: benchmarkName,
        chapterContent: benchmarkContent,
        expectedClaims: benchmarkExpectedClaims
      });

      if (response.data.success) {
        toast.success("Benchmark created successfully");
        setIsCreateBenchmarkModalOpen(false);
        setBenchmarkName("");
        setBenchmarkContent("");
        setBenchmarkExpectedClaims([]);
        await fetchBenchmarks();
      } else {
        toast.error("Failed to create benchmark");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to create benchmark");
    } finally {
      setIsCreatingBenchmark(false);
    }
  };

  const handleRunTest = async () => {
    if (!selectedBenchmarkId) {
      toast.error("Please select a benchmark");
      return;
    }

    setIsRunning(true);

    let contentToTest = "";
    let benchmarkIdToUse: number | undefined = undefined;

    try {
      const response = await $api.get(`/playground/benchmarks/${selectedBenchmarkId}`);
      if (response.data.success) {
        contentToTest = response.data.benchmark.chapterContent;
        benchmarkIdToUse = selectedBenchmarkId;
      } else {
        toast.error("Failed to load benchmark content");
        setIsRunning(false);
        return;
      }
    } catch (error) {
      toast.error("Failed to load benchmark content");
      setIsRunning(false);
      return;
    }

    try {
      const response = await $api.post("/playground/test-claims-extraction", {
        chapterContent: contentToTest,
        benchmarkId: benchmarkIdToUse
      });

      if (response.data.success) {
        setChapterContent(contentToTest);
        setComparison(response.data.comparison || null);
        setSelectedHighlight(null);
        setViewMode("detail");
        setIsNewTestModalOpen(false);
        toast.success(`Extracted ${response.data.claims.length} claims`);
        await fetchTests();
      } else {
        toast.error("Failed to extract claims");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred while extracting claims");
    } finally {
      setIsRunning(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return "Just now";
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString();
  };

  const renderContent = () => {
    if (viewMode === "benchmarks") {
      return <BenchmarkManagement onClose={() => setViewMode("list")} />;
    }

    if (viewMode === "list") {
      return (
        <div className="h-full flex flex-col">
          <div className="flex items-center justify-between mb-4 pb-4 border-b">
            <FigmaTypography component="h2" variant="memo-heading-2">Claims Extraction Tests</FigmaTypography>
            <div className="flex gap-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center gap-2">
                    <Gear className="w-4 h-4" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={handleManageBenchmarks} className="cursor-pointer">
                    Manage Benchmarks
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={handleCreateBenchmark} className="cursor-pointer">
                    Create Benchmark
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <button
                onClick={handleRunNewTest}
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                Run
              </button>
            </div>
          </div>

          {!isLoadingTests && tests.length > 0 && (
            <div className="flex gap-3 mb-4 w-full">
              <select
                value={filterBenchmark}
                onChange={(e) => setFilterBenchmark(e.target.value)}
                className="px-3 py-1.5 text-[14px] border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
              >
                <option value="all">All benchmarks</option>
                <option value="none">No benchmark</option>
                {[...new Set(tests.filter((t) => t.benchmarkName).map((t) => t.benchmarkName))].map((name) => (
                  <option key={name || ""} value={name || ""}>
                    {name}
                  </option>
                ))}
              </select>
              <Popover>
                <PopoverTrigger asChild>
                  <button className="px-3 py-1.5 text-[14px] border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:border-blue-500 flex items-center gap-2">
                    {filterDateFrom || filterDateTo ? (
                      <span>
                        {filterDateFrom || "Start"} → {filterDateTo || "End"}
                      </span>
                    ) : (
                      <span className="text-gray-500">Date range</span>
                    )}
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-3" align="start">
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <FigmaTypography component="label" variant="body-3" className="text-gray-500 w-10">From</FigmaTypography>
                      <input
                        type="date"
                        value={filterDateFrom}
                        onChange={(e) => setFilterDateFrom(e.target.value)}
                        className="px-2 py-1 text-[14px] border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <FigmaTypography component="label" variant="body-3" className="text-gray-500 w-10">To</FigmaTypography>
                      <input
                        type="date"
                        value={filterDateTo}
                        onChange={(e) => setFilterDateTo(e.target.value)}
                        className="px-2 py-1 text-[14px] border border-gray-300 rounded-md focus:outline-none focus:border-blue-500"
                      />
                    </div>
                    {(filterDateFrom || filterDateTo) && (
                      <button
                        onClick={() => {
                          setFilterDateFrom("");
                          setFilterDateTo("");
                        }}
                        className="text-[12px] leading-[18px] text-blue-600 hover:underline self-end"
                      >
                        Clear
                      </button>
                    )}
                  </div>
                </PopoverContent>
              </Popover>
              <div className="ml-auto flex items-center gap-4">
                {(() => {
                  const filtered = tests.filter((test) => {
                    if (filterBenchmark === "none" && test.benchmarkName) return false;
                    if (
                      filterBenchmark !== "all" &&
                      filterBenchmark !== "none" &&
                      test.benchmarkName !== filterBenchmark
                    )
                      return false;
                    if (filterDateFrom || filterDateTo) {
                      const testDate = new Date(test.createdAt);
                      if (filterDateFrom && testDate < new Date(filterDateFrom)) return false;
                      if (filterDateTo) {
                        const toDate = new Date(filterDateTo);
                        toDate.setHours(23, 59, 59, 999);
                        if (testDate > toDate) return false;
                      }
                    }
                    return true;
                  });
                  const withBenchmark = filtered.filter((t) => t.matchedClaims !== null && t.expectedClaims !== null);
                  const avgMatch =
                    withBenchmark.length > 0
                      ? Math.round(
                          (withBenchmark.reduce((sum, t) => sum + (t.matchedClaims || 0) / (t.expectedClaims || 1), 0) /
                            withBenchmark.length) *
                            100
                        )
                      : null;

                  return (
                    <div className="flex items-center gap-3 text-[14px] text-gray-500">
                      <span>{filtered.length} tests</span>
                      {withBenchmark.length > 0 && (
                        <>
                          <span className="text-gray-300">|</span>
                          <span
                            className={
                              avgMatch !== null && avgMatch >= 80
                                ? "text-green-600"
                                : avgMatch !== null && avgMatch >= 50
                                ? "text-orange-500"
                                : "text-red-500"
                            }
                          >
                            {avgMatch}% avg match
                          </span>
                        </>
                      )}
                    </div>
                  );
                })()}
                <button
                  onClick={() => setIsChartModalOpen(true)}
                  className="px-3 py-1.5 text-[14px] border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                >
                  Chart
                </button>
              </div>
            </div>
          )}

          {isLoadingTests ? (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <p>Loading test history...</p>
            </div>
          ) : tests.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
              <p className="text-lg mb-2">No tests yet</p>
              <p className="text-[14px]">Run your first extraction test to see results here</p>
            </div>
          ) : (
            <div className="flex-1 overflow-y-auto">
              <div className="space-y-2">
                {tests
                  .filter((test) => {
                    if (filterBenchmark === "none" && test.benchmarkName) return false;
                    if (
                      filterBenchmark !== "all" &&
                      filterBenchmark !== "none" &&
                      test.benchmarkName !== filterBenchmark
                    )
                      return false;

                    if (filterDateFrom || filterDateTo) {
                      const testDate = new Date(test.createdAt);
                      if (filterDateFrom) {
                        const fromDate = new Date(filterDateFrom);
                        fromDate.setHours(0, 0, 0, 0);
                        if (testDate < fromDate) return false;
                      }
                      if (filterDateTo) {
                        const toDate = new Date(filterDateTo);
                        toDate.setHours(23, 59, 59, 999);
                        if (testDate > toDate) return false;
                      }
                    }

                    return true;
                  })
                  .map((test) => (
                    <button
                      key={test.id}
                      onClick={() => handleLoadTest(test.id)}
                      className="w-full text-left px-3 py-2 rounded-lg border bg-white border-gray-200 hover:bg-gray-50 hover:border-gray-300 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          {test.benchmarkName && (
                            <span className="font-medium text-gray-900 truncate">{test.benchmarkName}</span>
                          )}
                          {test.expectedClaims !== null && test.matchedClaims !== null ? (
                            <FigmaTypography
                              component="span"
                              variant="body-3"
                              className={`px-2 py-0.5 rounded flex-shrink-0 ${
                                test.matchedClaims === test.expectedClaims
                                  ? "bg-green-100 text-green-700"
                                  : "bg-orange-100 text-orange-700"
                              }`}
                            >
                              {test.matchedClaims}/{test.expectedClaims}
                            </FigmaTypography>
                          ) : (
                            <FigmaTypography component="span" variant="body-3" className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded flex-shrink-0">
                              {test.totalClaims} claims
                            </FigmaTypography>
                          )}
                        </div>
                        <div className="flex items-center gap-2 text-[12px] leading-[18px] text-gray-400 flex-shrink-0">
                          <span>{formatDate(test.createdAt)}</span>
                          {test.executionTimeMs && <span>{(test.executionTimeMs / 1000).toFixed(2)}s</span>}
                        </div>
                      </div>
                    </button>
                  ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="h-full flex flex-col">
        <div className="flex items-center justify-between mb-4 pb-4 border-b">
          <div>
            <button
              onClick={() => setViewMode("list")}
              className="text-gray-600 hover:text-gray-800 transition-colors mb-2"
            >
              ← Back to Tests
            </button>
            <FigmaTypography component="h2" variant="memo-heading-2">Test Results</FigmaTypography>
          </div>
          <button
            onClick={handleRunNewTest}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
          >
            Run
          </button>
        </div>

        {comparison && (
          <div className="mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200 flex items-center gap-3">
            <span className="text-[14px] font-semibold">Matched Claims:</span>
            <span
              className={`text-lg font-semibold ${
                comparison.matchedCount === comparison.expectedCount ? "text-green-600" : "text-orange-600"
              }`}
            >
              {comparison.matchedCount ?? 0}/{comparison.expectedCount}
            </span>
            <span className="text-[14px] text-gray-500">
              {comparison.matchedCount === comparison.expectedCount
                ? "(all matched)"
                : `(${comparison.expectedCount - (comparison.matchedCount ?? 0)} missing)`}
            </span>
          </div>
        )}

        <div className="flex-1 flex gap-6 min-h-0">
          <div className="w-1/2 flex flex-col">
            <h3 className="text-[14px] font-semibold mb-2">Chapter Content</h3>
            <div className="flex-1 min-h-0">
              <SimpleMarkdownEditor
                content={chapterContent}
                onChange={setChapterContent}
                placeholder=""
                highlightText={selectedHighlight || undefined}
                readOnly
              />
            </div>
          </div>

          <div className="w-1/2 flex flex-col min-h-0">
            <h3 className="text-[14px] font-semibold mb-2">Claims Comparison</h3>
            {comparison?.claimComparisons && comparison.claimComparisons.length > 0 ? (
              <div className="flex-1 overflow-y-auto border rounded-lg bg-white">
                <table className="w-full text-[14px]">
                  <thead className="sticky top-0 bg-gray-50 border-b">
                    <tr>
                      <th className="text-left p-2 font-medium text-gray-700 w-1/2">Benchmark Claims</th>
                      <th className="text-left p-2 font-medium text-gray-700 w-1/2">Extracted Claims</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparison.claimComparisons.map((row, idx) => (
                      <tr
                        key={idx}
                        onClick={() => {
                          setSelectedHighlight(row.actualClaimMd || row.benchmarkClaimMd);
                        }}
                        className={`border-b cursor-pointer hover:bg-gray-50 ${
                          selectedHighlight === (row.actualClaimMd || row.benchmarkClaimMd) ? "bg-blue-50" : ""
                        }`}
                      >
                        <td className="p-2 align-top">
                          {row.benchmarkClaim ? (
                            <div className="flex items-start gap-2">
                              {row.matched ? (
                                <span className="text-green-600 mt-0.5">✓</span>
                              ) : (
                                <span className="text-red-500 mt-0.5">✗</span>
                              )}
                              <span className="text-gray-900">{row.benchmarkClaim}</span>
                            </div>
                          ) : (
                            <span className="text-gray-400 italic">—</span>
                          )}
                        </td>
                        <td className="p-2 align-top">
                          {row.actualClaim ? (
                            <div className="flex items-start gap-2">
                              {row.benchmarkClaim ? (
                                <span className="text-green-600 mt-0.5">✓</span>
                              ) : (
                                <span className="text-orange-500 mt-0.5">+</span>
                              )}
                              <span className="text-gray-900">{row.actualClaim}</span>
                            </div>
                          ) : (
                            <span className="text-gray-400 italic">—</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-500 border rounded-lg bg-white">
                <p>No comparison data available</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  console.log(benchmarkExpectedClaims);

  return (
    <>
      {renderContent()}

      <Dialog open={isNewTestModalOpen} onOpenChange={setIsNewTestModalOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Run New Test</DialogTitle>
            <DialogDescription>Select a benchmark to test against.</DialogDescription>
          </DialogHeader>

          {isLoadingBenchmarks ? (
            <div className="flex items-center justify-center py-8 text-gray-500">
              <p>Loading benchmarks...</p>
            </div>
          ) : benchmarks.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-gray-500">
              <p className="mb-2">No benchmarks available</p>
              <p className="text-[14px]">Create a benchmark first to run tests</p>
            </div>
          ) : (
            <div className="max-h-[400px] overflow-y-auto border rounded-lg p-2 space-y-2">
              {benchmarks.map((benchmark) => (
                <button
                  key={benchmark.id}
                  onClick={() => setSelectedBenchmarkId(benchmark.id)}
                  className={`w-full text-left p-3 rounded-md border transition-colors ${
                    selectedBenchmarkId === benchmark.id
                      ? "bg-blue-50 border-blue-300"
                      : "bg-white border-gray-200 hover:bg-gray-50"
                  }`}
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[14px] font-semibold text-gray-900">{benchmark.name}</span>
                    <FigmaTypography component="span" variant="body-3" className="text-gray-500">{formatDate(benchmark.createdAt)}</FigmaTypography>
                  </div>
                  <FigmaTypography variant="body-3" className="text-gray-600 mb-1 line-clamp-2">
                    {benchmark.chapterContentPreview}
                  </FigmaTypography>
                  <FigmaTypography component="span" variant="body-3" className="text-gray-500">
                    {benchmark.expectedClaimsCount} expected claims
                  </FigmaTypography>
                </button>
              ))}
            </div>
          )}

          <DialogFooter>
            <button
              onClick={() => setIsNewTestModalOpen(false)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleRunTest}
              disabled={isRunning || !selectedBenchmarkId}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isRunning ? "Running..." : "Run Test"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={isCreateBenchmarkModalOpen}
        onOpenChange={(open) => {
          setIsCreateBenchmarkModalOpen(open);
          if (!open) {
            setSelectedBenchmarkClaimIndex(null);
          }
        }}
      >
        <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Create Benchmark</DialogTitle>
            <DialogDescription>Set up a benchmark with expected claims for testing.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 overflow-y-auto flex-1">
            <div>
              <label className="block text-[14px] font-medium text-gray-700 mb-1">Benchmark Name</label>
              <input
                type="text"
                value={benchmarkName}
                onChange={(e) => setBenchmarkName(e.target.value)}
                placeholder="e.g., Standard Business Chapter Test"
                disabled={isCreatingBenchmark}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:border-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
              />
            </div>

            <div>
              <label className="block text-[14px] font-medium text-gray-700 mb-1">Chapter Content</label>
              <SimpleMarkdownEditor
                content={benchmarkContent}
                onChange={setBenchmarkContent}
                placeholder="Enter the markdown content for this benchmark..."
                highlightText={
                  selectedBenchmarkClaimIndex !== null && benchmarkExpectedClaims[selectedBenchmarkClaimIndex]
                    ? benchmarkExpectedClaims[selectedBenchmarkClaimIndex].claimmd
                    : undefined
                }
                readOnly={isCreatingBenchmark}
              />
              <FigmaTypography variant="body-3" className="text-gray-500 mt-1">{benchmarkContent.length} characters</FigmaTypography>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <button
                  onClick={handleGenerateExpectedClaims}
                  disabled={isCreatingBenchmark || !benchmarkContent.trim()}
                  className="px-3 py-1.5 text-[14px] bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {isCreatingBenchmark ? "Extracting..." : "Extract Claims"}
                </button>
              </div>

              {isAddingClaim && (
                <div className="mb-3 p-3 border border-green-300 rounded-lg bg-green-50">
                  <label className="block text-[14px] font-medium text-gray-700 mb-1">New Claim</label>
                  <input
                    type="text"
                    value={newClaimText}
                    onChange={(e) => setNewClaimText(e.target.value)}
                    placeholder="Enter claim text..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 mb-2"
                    onKeyDown={(e) => {
                      if (isEnterSubmit(e, { allowShift: true })) handleAddClaim();
                      if (e.key === "Escape") {
                        setIsAddingClaim(false);
                        setNewClaimText("");
                      }
                    }}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={handleAddClaim}
                      className="px-3 py-1 text-[14px] bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                    >
                      Add
                    </button>
                    <button
                      onClick={() => {
                        setIsAddingClaim(false);
                        setNewClaimText("");
                      }}
                      className="px-3 py-1 text-[14px] bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {benchmarkExpectedClaims.length > 0 ? (
                <div className="border rounded-lg p-3 bg-gray-50">
                  <p className="text-[14px] text-gray-600 mb-2">
                    {benchmarkExpectedClaims.length} claims. Click on any claim to highlight it in the content above.
                  </p>
                  <div className="flex items-center gap-2">
                    <label className="block text-[14px] font-medium text-gray-700">Expected Claims</label>
                    <button
                      onClick={() => setIsAddingClaim(true)}
                      disabled={isCreatingBenchmark}
                      className="p-1.5 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      title="Add claim"
                    >
                      <Plus size={18} weight="bold" />
                    </button>
                  </div>
                  <div className="max-h-[200px] overflow-y-auto space-y-1">
                    {benchmarkExpectedClaims.map((claim, idx) => (
                      <div key={idx}>
                        {editingClaimIndex === idx ? (
                          <div className="p-3 border border-blue-300 rounded bg-blue-50">
                            <label className="block text-[12px] leading-[18px] font-medium text-gray-700 mb-1">
                              Edit Claim Markdown (this exact text will be matched in the content)
                            </label>
                            <textarea
                              value={editingClaimText}
                              onChange={(e) => setEditingClaimText(e.target.value)}
                              className="w-full px-3 py-2 text-[14px] border border-gray-300 rounded focus:outline-none focus:border-blue-500 mb-2 font-mono min-h-[80px]"
                              onKeyDown={(e) => {
                                if (e.key === "Escape") handleCancelClaimEdit();
                              }}
                              placeholder="Enter the exact markdown text to match..."
                            />
                            <div className="flex gap-2">
                              <button
                                onClick={handleSaveClaimEdit}
                                className="px-3 py-1.5 text-[12px] leading-[18px] bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                              >
                                Save
                              </button>
                              <button
                                onClick={handleCancelClaimEdit}
                                className="px-3 py-1.5 text-[12px] leading-[18px] bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition-colors"
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleSelectBenchmarkClaim(idx)}
                              className={`flex-1 text-left text-[12px] leading-[18px] p-2 rounded border transition-colors ${
                                selectedBenchmarkClaimIndex === idx
                                  ? "bg-blue-50 border-blue-300"
                                  : "bg-white border-gray-200 hover:bg-gray-50"
                              }`}
                            >
                              {claim.claimmd}
                            </button>
                            <button
                              onClick={() => handleStartEditClaim(idx)}
                              className="p-1.5 text-gray-600 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Edit claim"
                            >
                              <PencilSimple size={16} weight="regular" />
                            </button>
                            <button
                              onClick={() => handleDeleteClaim(idx)}
                              className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete claim"
                            >
                              <Trash size={16} weight="regular" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-[14px] text-gray-500">
                  Click "Extract Claims" to generate claims from the content, or the + icon to add manually.
                </p>
              )}
            </div>
          </div>

          <DialogFooter>
            <button
              onClick={() => setIsCreateBenchmarkModalOpen(false)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveBenchmark}
              disabled={
                isCreatingBenchmark ||
                !benchmarkName.trim() ||
                !benchmarkContent.trim() ||
                benchmarkExpectedClaims.length === 0
              }
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isCreatingBenchmark ? "Saving..." : "Save Benchmark"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={isChartModalOpen} onOpenChange={setIsChartModalOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Test Performance</DialogTitle>
            <DialogDescription>Match rate over time for tests with benchmarks</DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {(() => {
              const testsWithBenchmark = tests
                .filter((t) => t.expectedClaims !== null && t.matchedClaims !== null)
                .sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

              if (testsWithBenchmark.length === 0) {
                return (
                  <div className="text-center text-gray-500 py-8">
                    No tests with benchmarks yet. Run tests against benchmarks to see performance data.
                  </div>
                );
              }

              const chartWidth = 800;
              const chartHeight = 350;
              const padding = { top: 40, right: 30, bottom: 60, left: 60 };
              const innerWidth = chartWidth - padding.left - padding.right;
              const innerHeight = chartHeight - padding.top - padding.bottom;

              // Group tests by benchmark
              const benchmarkGroups: Record<string, typeof testsWithBenchmark> = {};
              testsWithBenchmark.forEach((t) => {
                const name = t.benchmarkName || "Unknown Benchmark";
                if (!benchmarkGroups[name]) benchmarkGroups[name] = [];
                benchmarkGroups[name].push(t);
              });

              const benchmarkNames = Object.keys(benchmarkGroups);
              const benchmarkColors = [
                "#3b82f6", // blue
                "#10b981", // green
                "#f59e0b", // amber
                "#ef4444", // red
                "#8b5cf6", // purple
                "#ec4899", // pink
                "#06b6d4" // cyan
              ];

              const minTime = new Date(testsWithBenchmark[0].createdAt).getTime();
              const maxTime = new Date(testsWithBenchmark[testsWithBenchmark.length - 1].createdAt).getTime();
              const timeRange = maxTime - minTime || 1;

              const getX = (dateStr: string) => {
                const t = new Date(dateStr).getTime();
                return padding.left + ((t - minTime) / timeRange) * innerWidth;
              };

              const getY = (matchRate: number) => {
                return padding.top + innerHeight - (matchRate / 100) * innerHeight;
              };

              const getLinePath = (pts: { x: number; y: number }[]) => {
                return pts.map((p, i) => `${i === 0 ? "M" : "L"} ${p.x} ${p.y}`).join(" ");
              };

              return (
                <div className="space-y-6">
                  <div className="bg-white p-4 rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                    <svg
                      viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                      className="w-full h-auto overflow-visible"
                      style={{ filter: "drop-shadow(0 4px 6px -1px rgb(0 0 0 / 0.05))" }}
                    >
                      <defs>
                        {benchmarkNames.map((name, i) => (
                          <linearGradient key={`grad-${i}`} id={`areaGradient-${i}`} x1="0" y1="0" x2="0" y2="1">
                            <stop
                              offset="0%"
                              stopColor={benchmarkColors[i % benchmarkColors.length]}
                              stopOpacity="0.1"
                            />
                            <stop
                              offset="100%"
                              stopColor={benchmarkColors[i % benchmarkColors.length]}
                              stopOpacity="0"
                            />
                          </linearGradient>
                        ))}
                      </defs>

                      {/* Grid Lines */}
                      {[0, 25, 50, 75, 100].map((pct) => (
                        <g key={pct}>
                          <line
                            x1={padding.left}
                            y1={padding.top + innerHeight - (pct / 100) * innerHeight}
                            x2={padding.left + innerWidth}
                            y2={padding.top + innerHeight - (pct / 100) * innerHeight}
                            stroke="#f1f5f9"
                            strokeWidth={1}
                            strokeDasharray={pct === 0 ? "0" : "4 4"}
                          />
                          <text
                            x={padding.left - 12}
                            y={padding.top + innerHeight - (pct / 100) * innerHeight + 4}
                            textAnchor="end"
                            className="text-[12px] font-medium fill-gray-400"
                          >
                            {pct}%
                          </text>
                        </g>
                      ))}

                      {/* Benchmark Lines and Areas */}
                      {benchmarkNames.map((name, bIdx) => {
                        if (hiddenBenchmarks.has(name)) return null;

                        const group = benchmarkGroups[name];
                        const pts = group.map((t) => ({
                          x: getX(t.createdAt),
                          y: getY(((t.matchedClaims || 0) / (t.expectedClaims || 1)) * 100),
                          matchRate: ((t.matchedClaims || 0) / (t.expectedClaims || 1)) * 100,
                          test: t
                        }));

                        const linePath = getLinePath(pts);
                        const areaPath =
                          pts.length > 1
                            ? `${linePath} L ${pts[pts.length - 1].x} ${padding.top + innerHeight} L ${pts[0].x} ${
                                padding.top + innerHeight
                              } Z`
                            : "";

                        const color = benchmarkColors[bIdx % benchmarkColors.length];

                        return (
                          <g key={name} className="animate-in fade-in duration-300">
                            {areaPath && (
                              <path
                                d={areaPath}
                                fill={`url(#areaGradient-${bIdx})`}
                                className="transition-opacity duration-300 hover:opacity-100 opacity-60"
                              />
                            )}
                            <path
                              d={linePath}
                              fill="none"
                              stroke={color}
                              strokeWidth={2.5}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              className="transition-all duration-300 hover:stroke-width-4 opacity-80 hover:opacity-100"
                            />
                            {pts.map((p, pIdx) => (
                              <g key={pIdx} className="group cursor-pointer">
                                <circle
                                  cx={p.x}
                                  cy={p.y}
                                  r={4}
                                  fill={color}
                                  stroke="#fff"
                                  strokeWidth={1.5}
                                  className="transition-all duration-200 group-hover:r-6 shadow-sm"
                                />
                                <title>
                                  {p.test.benchmarkName}
                                  {"\n"}Match Rate: {Math.round(p.matchRate)}%{"\n"}Date:{" "}
                                  {new Date(p.test.createdAt).toLocaleString()}
                                </title>
                              </g>
                            ))}
                          </g>
                        );
                      })}

                      {/* X-Axis Time Labels */}
                      {[0, 0.25, 0.5, 0.75, 1].map((tick) => {
                        const time = minTime + tick * timeRange;
                        const x = padding.left + tick * innerWidth;
                        return (
                          <g key={tick}>
                            <line
                              x1={x}
                              y1={padding.top + innerHeight}
                              x2={x}
                              y2={padding.top + innerHeight + 5}
                              stroke="#e2e8f0"
                              strokeWidth={1}
                            />
                            <text
                              x={x}
                              y={padding.top + innerHeight + 20}
                              textAnchor="middle"
                              className="text-[10px] fill-gray-500 font-medium"
                            >
                              {new Date(time).toLocaleDateString("en-US", {
                                month: "short",
                                day: "numeric"
                              })}
                            </text>
                            <text
                              x={x}
                              y={padding.top + innerHeight + 34}
                              textAnchor="middle"
                              className="text-[10px] fill-gray-400"
                            >
                              {new Date(time).toLocaleTimeString("en-US", {
                                hour: "2-digit",
                                minute: "2-digit",
                                hour12: true
                              })}
                            </text>
                          </g>
                        );
                      })}
                    </svg>
                  </div>

                  {/* Legend */}
                  <div className="flex flex-wrap items-center gap-4 justify-center bg-gray-50/50 p-4 rounded-xl border border-gray-100">
                    {benchmarkNames.map((name, i) => {
                      const isHidden = hiddenBenchmarks.has(name);
                      return (
                        <button
                          key={name}
                          onClick={() => toggleBenchmarkVisibility(name)}
                          className={`flex items-center gap-2 px-3 py-1.5 rounded-full transition-all duration-200 border ${
                            isHidden
                              ? "bg-white border-gray-200 opacity-50 grayscale"
                              : "bg-white border-gray-200 shadow-sm hover:border-gray-300 active:scale-95"
                          }`}
                        >
                          <div
                            className="w-2.5 h-2.5 rounded-full"
                            style={{ backgroundColor: benchmarkColors[i % benchmarkColors.length] }}
                          />
                          <span
                            className={`text-[12px] leading-[18px] font-semibold ${
                              isHidden ? "text-gray-400" : "text-gray-700"
                            }`}
                          >
                            {name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })()}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
